/**
 * Manifest validation + contribution-point aggregation. Pure functions only
 * — no React, no store access — so it can be unit-tested and reused by both
 * the dev loader (dev/localExtensions.js) and, in Phase 4, a fetch-based
 * marketplace client without change.
 */

/**
 * `ui.openModal` renders a dialog inside the app's real Modal chrome, with
 * the app's real styling, on top of whatever the user was doing. That is a
 * phishing primitive, so it is a consented capability rather than an ambient
 * one — an extension that declared `permissions: []` (and therefore showed
 * the user an empty consent screen) must not be able to raise an app-native
 * dialog at all.
 */
export const UI_MODAL_CAPABILITY = 'ui:modal';

/**
 * Separate, louder consent for the `password` field type. "Show me a form" and
 * "ask the user to type a secret into a form I wrote" are different asks, and
 * the second is the one that actually harvests credentials, so it gets its own
 * row on the consent screen instead of riding along with `ui:modal`.
 */
export const UI_PASSWORD_FIELD_CAPABILITY = 'ui:passwordField';

// Closed set of capability strings extensions may request. Extending this
// list is a deliberate host-side decision — manifests can't invent new
// capabilities and have them silently granted.
export const KNOWN_CAPABILITIES = new Set([
  'db:mysql',
  'db:postgres',
  'db:sqlite',
  // Saved SQL scripts on disk. Separate from the engine grants because it is a
  // different thing to be trusted with: a script file holds schema, table
  // names and sometimes literal credentials, and reading them needs no live
  // connection at all.
  'db:scripts',
  'secureStorage',
  UI_MODAL_CAPABILITY,
  UI_PASSWORD_FIELD_CAPABILITY,
]);

// Same shape the backend enforces for marketplace slugs (extension.ex) —
// lowercase reverse-DNS, e.g. "com.apiboo.db-manager". Beyond consistency,
// this guarantees ids can never contain ":", which native-resource scoping
// uses as its key separator (db_mysql.rs pools are keyed "{id}:{connId}" and
// disconnect_all matches on the "{id}:" prefix — an id containing ":" could
// craft keys that collide with another extension's namespace).
const MANIFEST_ID_RE = /^[a-z0-9]+(?:\.[a-z0-9-]+)*$/;

export function validateManifest(manifest) {
  const errors = [];
  if (!manifest || typeof manifest !== 'object') {
    return { valid: false, errors: ['Manifest must be an object'] };
  }
  if (manifest.schemaVersion !== 1) errors.push('Unsupported schemaVersion');
  if (!manifest.id || typeof manifest.id !== 'string') errors.push('Missing "id"');
  else if (!MANIFEST_ID_RE.test(manifest.id)) errors.push('"id" must be a lowercase reverse-DNS style id (e.g. "com.example.my-extension")');
  if (!manifest.name || typeof manifest.name !== 'string') errors.push('Missing "name"');
  if (!manifest.version || typeof manifest.version !== 'string') errors.push('Missing "version"');
  if (!manifest.entry || typeof manifest.entry !== 'string') errors.push('Missing "entry"');

  for (const perm of manifest.permissions || []) {
    if (!KNOWN_CAPABILITIES.has(perm.capability)) {
      errors.push(`Unknown capability "${perm.capability}"`);
    }
  }

  const activityBar = manifest.contributes?.activityBar || [];
  for (const entry of activityBar) {
    if (!entry.id) errors.push('activityBar contribution missing "id"');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Aggregates contribution points across all enabled extensions into
 * lookup-friendly shapes consumed by activityBarRegistry.js, AppContent.js,
 * and Sidebar.js.
 *
 * @param {Array<{ manifest: object, enabled: boolean }>} entries
 */
export function aggregateContributions(entries) {
  const activityBarEntries = [];
  const panelsByActivityBarId = new Map();
  const sidebarSectionsByActivityBarId = new Map();
  const settingsContributions = [];

  for (const { manifest, enabled } of entries) {
    if (!enabled) continue;
    const contributes = manifest.contributes || {};

    for (const entry of contributes.activityBar || []) {
      activityBarEntries.push({
        id: entry.id,
        extensionId: manifest.id,
        title: entry.title,
        icon: entry.icon,
        order: entry.order || {},
      });
    }

    for (const panel of contributes.panels || []) {
      panelsByActivityBarId.set(panel.activityBarId, {
        id: panel.id,
        extensionId: manifest.id,
        title: panel.title,
      });
    }

    for (const section of contributes.sidebarSections || []) {
      sidebarSectionsByActivityBarId.set(section.activityBarId, {
        id: section.id,
        extensionId: manifest.id,
        title: section.title,
        icon: section.icon,
        headerActions: section.headerActions || [],
      });
    }

    for (const setting of contributes.settings || []) {
      settingsContributions.push({
        id: setting.id,
        extensionId: manifest.id,
        title: setting.title,
        icon: setting.icon,
      });
    }
  }

  return {
    activityBarEntries,
    panelsByActivityBarId,
    sidebarSectionsByActivityBarId,
    settingsContributions,
  };
}
